import React, { useState, useEffect, useRef, useCallback, Fragment } from "react";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { dashboardToast } from "../../utils/dashboardToast";
import "bootstrap/dist/css/bootstrap.min.css";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import { useMenuState } from "../../Common/MenuStateContext";
import SaListPageShell from "../../components/sa/SaListPageShell";
import ContentLoader from "../../components/ContentLoader";
import RecordDetailModal from "../../components/RecordDetailModal";
import TableSortSelect from "../../components/TableSortSelect";
import { Alert, Button as UiButton, Pagination as UiPagination } from "../../components/ui";
import { TABLE_SORT } from "../../utils/tableSort";
import { getFunctionalValueDrivers, addOrUpdateFunctionalValueDriver, deleteFunctionalValueDriver } from "../../services/CostEstimationsetupApi";
import { ChevronDown, ChevronRight, Layers, Pencil, Plus, Search, Settings, Trash2 } from "lucide-react";
import "../../assets/css/dashboard.css";

const EmptyCell = () => <span className="app-td__empty">—</span>;

const FunctionalValueDrivers = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewMode, setViewMode] = useState("parent"); // "parent" | "child"
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(""); // "functionalValueDriver" or "subDriver"
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Functional Value Drivers form data
  const [functionalValueDriverForm, setFunctionalValueDriverForm] = useState({
    id: 0,
    name: "",
    description: "",
    isActive: true,
    functionalSubValueDrivers: []
  });

  // Functional Value Drivers data from API
  const [functionalValueDrivers, setFunctionalValueDrivers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState(TABLE_SORT.DATE_DESC);
  const dataFetchedRef = useRef(false);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const [apiPageInfo, setApiPageInfo] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalPages: 1,
    totalCount: 0,
  });

  // Functional Value Drivers handlers
  const handleShowFunctionalValueDriver = useCallback((functionalValueDriver = null) => {
    if (functionalValueDriver) {
      setFunctionalValueDriverForm({
        id: functionalValueDriver.id,
        name: functionalValueDriver.name || "",
        description: functionalValueDriver.description || "",
        isActive: functionalValueDriver.isActive !== undefined ? functionalValueDriver.isActive : true,
        functionalSubValueDrivers: functionalValueDriver.functionalSubValueDrivers || []
      });
    } else {
      setFunctionalValueDriverForm({
        id: 0,
        name: "",
        description: "",
        isActive: true,
        functionalSubValueDrivers: []
      });
    }
    setShowModal(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowModal(false);
    setFunctionalValueDriverForm({
      id: 0,
      name: "",
      description: "",
      isActive: true,
      functionalSubValueDrivers: []
    });
  }, []);

  const handleFunctionalValueDriverChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFunctionalValueDriverForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);

  const handleSubDriverFieldChange = useCallback((index, field, value) => {
    setFunctionalValueDriverForm(prev => ({
      ...prev,
      functionalSubValueDrivers: prev.functionalSubValueDrivers.map((subDriver, i) =>
        i === index ? { ...subDriver, [field]: value } : subDriver
      )
    }));
  }, []);

  const handleAddSubDriver = useCallback(() => {
    setFunctionalValueDriverForm(prev => ({
      ...prev,
      functionalSubValueDrivers: [
        ...prev.functionalSubValueDrivers,
        {
          id: 0,
          name: "",
          description: "",
          isActive: true
        }
      ]
    }));
  }, []);

  const handleRemoveSubDriver = useCallback((index) => {
    setFunctionalValueDriverForm(prev => ({
      ...prev,
      functionalSubValueDrivers: prev.functionalSubValueDrivers.filter((_, i) => i !== index)
    }));
  }, []);

  const handleFunctionalValueDriverSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const { name, description, functionalSubValueDrivers } = functionalValueDriverForm;
      if (name) {
        try {
          setLoading(true);
          
          // Prepare API data
          const apiData = {
            id: functionalValueDriverForm.id,
            name: name.trim(),
            description: description ? description.trim() : '',
            isActive: functionalValueDriverForm.isActive,
            functionalSubValueDrivers: functionalSubValueDrivers
              .filter(subDriver => subDriver.name && subDriver.name.trim())
              .map(subDriver => ({
                id: subDriver.id || 0,
                functionalValueDriverId: functionalValueDriverForm.id || 0,
                name: subDriver.name.trim(),
                description: subDriver.description ? subDriver.description.trim() : '',
                isActive: subDriver.isActive !== undefined ? subDriver.isActive : true
              }))
          };

          const response = await addOrUpdateFunctionalValueDriver(apiData);
          
          if (response && response.succeeded !== false) {
            // Reload the data from API
            const refreshResponse = await getFunctionalValueDrivers({
              searchTerm: searchTerm,
              isActive: true,
              page: apiPageInfo.pageNumber,
              pageSize: apiPageInfo.pageSize,
              sort: sortKey,
            });
            
            if (refreshResponse && refreshResponse.data) {
              setFunctionalValueDrivers(refreshResponse.data.data || []);
              setApiPageInfo(prev => ({
                ...prev,
                totalPages: refreshResponse.data.totalPages || 1,
                totalCount: refreshResponse.data.totalRecords || 0
              }));
            }
            
            if (functionalValueDriverForm.id === 0) {
              dashboardToast.success("Functional Value Driver created successfully");
            } else {
              dashboardToast.success("Functional Value Driver updated successfully");
            }
            handleClose();
          } else {
            throw new Error(response?.message || 'Failed to save Functional Value Driver');
          }
        } catch (error) {
          console.error("Error saving Functional Value Driver:", error);
          dashboardToast.error(error.message || "Error saving Functional Value Driver");
        } finally {
          setLoading(false);
        }
      } else {
        dashboardToast.error("Please fill in all required fields");
      }
    },
    [functionalValueDriverForm, handleClose, apiPageInfo.pageNumber, apiPageInfo.pageSize, searchTerm, sortKey]
  );

  const handleDelete = useCallback((item) => {
    setItemToDelete(item);
    setDeleteType("functionalValueDriver");
    setShowDeleteModal(true);
  }, []);

  const handleDeleteSubDriver = useCallback((subDriverIndex) => {
    setItemToDelete({ index: subDriverIndex });
    setDeleteType("subDriver");
    setShowDeleteModal(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (itemToDelete) {
      try {
        setLoading(true);
        
        if (deleteType === "functionalValueDriver") {
          // Delete main functional value driver
          await deleteFunctionalValueDriver(itemToDelete.id, false);
          
          // Reload data from API
          const refreshResponse = await getFunctionalValueDrivers({
            searchTerm: searchTerm,
            isActive: true,
            page: apiPageInfo.pageNumber,
            pageSize: apiPageInfo.pageSize,
            sort: sortKey,
          });
          
          if (refreshResponse && refreshResponse.data) {
            setFunctionalValueDrivers(refreshResponse.data.data || []);
            setApiPageInfo(prev => ({
              ...prev,
              totalPages: refreshResponse.data.totalPages || 1,
              totalCount: refreshResponse.data.totalRecords || 0
            }));
          }
          
          dashboardToast.success("Functional Value Driver deleted successfully");
        } else if (deleteType === "subDriver") {
          // Get the sub-driver to check if it has an ID
          const subDriverToDelete = functionalValueDriverForm.functionalSubValueDrivers[itemToDelete.index];
          
          if (subDriverToDelete && subDriverToDelete.id && subDriverToDelete.id > 0) {
            // Delete sub-driver via API if it has an ID
            await deleteFunctionalValueDriver(subDriverToDelete.id, true);
            dashboardToast.success("Sub Functional Value Driver deleted successfully");
          } else {
            // Just remove from form if it's a new sub-driver without ID
            dashboardToast.success("Sub Functional Value Driver removed from form");
          }
          
          // Remove from form state
          setFunctionalValueDriverForm(prev => ({
            ...prev,
            functionalSubValueDrivers: prev.functionalSubValueDrivers.filter((_, i) => i !== itemToDelete.index)
          }));
        }
        
        setShowDeleteModal(false);
        setItemToDelete(null);
        setDeleteType("");
      } catch (error) {
        console.error("Error deleting Functional Value Driver:", error);
        dashboardToast.error(error.message || "Error deleting Functional Value Driver");
      } finally {
        setLoading(false);
      }
    }
  }, [itemToDelete, deleteType, apiPageInfo.pageNumber, apiPageInfo.pageSize, searchTerm, sortKey]);

  const cancelDelete = useCallback(() => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteType("");
  }, []);

  const openViewModal = useCallback((record, mode = "parent") => {
    setViewRecord(record);
    setViewMode(mode);
    setShowViewModal(true);
  }, []);

  const closeViewModal = useCallback(() => {
    setShowViewModal(false);
    setViewRecord(null);
    setViewMode("parent");
  }, []);

  // Pagination handlers
  const loadFunctionalValueDrivers = useCallback(async (page = apiPageInfo.pageNumber, pageSize = apiPageInfo.pageSize, sort = sortKey, search = searchTerm) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await getFunctionalValueDrivers({
        searchTerm: search,
        isActive: true,
        page,
        pageSize,
        sort,
      });
      
      if (response && response.data) {
        setFunctionalValueDrivers(response.data.data || []);
        setApiPageInfo(prev => ({
          ...prev,
          pageNumber: page,
          pageSize,
          totalPages: Math.max(1, response.data.totalPages || 1),
          totalCount: response.data.totalRecords || 0
        }));
      } else {
        setFunctionalValueDrivers([]);
      }
    } catch (err) {
      console.error('Error loading Functional Value Drivers:', err);
      setError(err.message || 'Failed to load Functional Value Drivers');
      dashboardToast.error('Failed to load Functional Value Drivers');
      setFunctionalValueDrivers([]);
    } finally {
      setLoading(false);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.pageSize, sortKey, searchTerm]);

  const goToPage = useCallback((pageNumber) => {
    const pg = Math.min(Math.max(pageNumber, 1), apiPageInfo.totalPages);
    if (pg !== apiPageInfo.pageNumber) {
      loadFunctionalValueDrivers(pg, apiPageInfo.pageSize);
    }
  }, [apiPageInfo.pageNumber, apiPageInfo.totalPages, apiPageInfo.pageSize, loadFunctionalValueDrivers]);

  // Load data on component mount
  useEffect(() => {
    loadFunctionalValueDrivers(apiPageInfo.pageNumber, apiPageInfo.pageSize);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!dataFetchedRef.current) {
      dataFetchedRef.current = true;
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      loadFunctionalValueDrivers(1, apiPageInfo.pageSize, sortKey, searchTerm);
    }, 300);

    return () => window.clearTimeout(timerId);
    // eslint-disable-next-line
  }, [searchTerm]);

  const handleSortChange = (nextSort) => {
    setSortKey(nextSort);
    loadFunctionalValueDrivers(1, apiPageInfo.pageSize, nextSort, searchTerm);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Toggle row expansion
  const toggleRowExpansion = (functionalValueDriverId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(functionalValueDriverId)) {
        newSet.delete(functionalValueDriverId);
      } else {
        newSet.add(functionalValueDriverId);
      }
      return newSet;
    });
  };

  // Get sub drivers for a specific Functional Value Driver
  const getSubDrivers = (functionalValueDriver) => {
    return functionalValueDriver.functionalSubValueDrivers || [];
  };

  // Get sub drivers count for a Functional Value Driver
  const getSubDriversCount = (functionalValueDriver) => {
    return (functionalValueDriver.functionalSubValueDrivers || []).length;
  };


  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={
        <Menu
          onSelect={handleMenuSelection}
          selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />
      }
    >
      <SaListPageShell topBarTitle="Functional Value Drivers Management">
        <div className="dash-frame-parent1">
          <div className="dash-list-toolbar">
            <div className="dash-list-toolbar__intro">
              <h5 className="dash-list-toolbar__title">Manage Functional Value Drivers</h5>
              <p className="dash-list-toolbar__desc">
                Create and manage functional value drivers and their sub-drivers.
              </p>
            </div>
            <div className="dash-list-toolbar__actions">
              <div className="dash-search-field dash-table-search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search by name…"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="dash-search-field__input"
                  disabled={loading}
                />
              </div>
              <TableSortSelect variant="chip"
                value={sortKey}
                onChange={handleSortChange}
                disabled={loading}
              />
              <UiButton
                variant="primary"
                className="sa-create-btn"
                onClick={() => handleShowFunctionalValueDriver()}
              >
                Create Driver
              </UiButton>
            </div>
          </div>

          {loading ? (
            <ContentLoader label="Loading functional value drivers…" />
          ) : error ? (
            <Alert variant="error">{error}</Alert>
          ) : (
            <Fragment>
              <div className="app-table-wrap app-table-wrap--refined">
                <table className="table table-striped">
                  <thead>
                    <tr>
                      <th aria-label="Expand" />
                      <th>#</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {functionalValueDrivers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center">
                          <span className="app-td__desc">No functional value drivers found.</span>
                        </td>
                      </tr>
                    ) : (
                      functionalValueDrivers.map((functionalValueDriver, index) => {
                        const subDriversList = getSubDrivers(functionalValueDriver);
                        const isExpanded = expandedRows.has(functionalValueDriver.id);
                        const displayName = functionalValueDriver.name || "Untitled driver";

                        return (
                          <Fragment key={functionalValueDriver.id}>
                            <tr
                              className="rdm-row-clickable"
                              onClick={() => openViewModal(functionalValueDriver, "parent")}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                {subDriversList.length > 0 ? (
                                  <button
                                    type="button"
                                    className="vd-expand-btn"
                                    onClick={() => toggleRowExpansion(functionalValueDriver.id)}
                                    title={isExpanded ? "Collapse sub-drivers" : "Expand sub-drivers"}
                                    aria-expanded={isExpanded}
                                    aria-label={
                                      isExpanded
                                        ? `Collapse ${displayName}`
                                        : `Expand ${displayName}`
                                    }
                                  >
                                    {isExpanded ? <ChevronDown aria-hidden /> : <ChevronRight aria-hidden />}
                                  </button>
                                ) : null}
                              </td>
                              <td>
                                <span className="app-td__index">{index + 1}</span>
                              </td>
                              <td>
                                <span className="app-td__title">{displayName}</span>
                              </td>
                              <td>
                                {functionalValueDriver.description ? (
                                  <span className="app-td__desc" title={functionalValueDriver.description}>
                                    {functionalValueDriver.description}
                                  </span>
                                ) : (
                                  <EmptyCell />
                                )}
                              </td>
                              <td>
                                <span className="app-td__meta">Functional</span>
                              </td>
                              <td>
                                <span
                                  className={
                                    functionalValueDriver.isActive
                                      ? "app-status app-status--success"
                                      : "app-status app-status--muted"
                                  }
                                >
                                  {functionalValueDriver.isActive ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td
                                className="dash-mobile-table-actions"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="btn-list-actions">
                                  <UiButton
                                    variant="outline"
                                    size="sm"
                                    className="btn-list-edit"
                                    title="Edit"
                                    aria-label={`Edit ${displayName}`}
                                    onClick={() =>
                                      handleShowFunctionalValueDriver(functionalValueDriver)
                                    }
                                  >
                                    <Pencil />
                                  </UiButton>
                                  <UiButton
                                    variant="danger"
                                    size="sm"
                                    className="btn-list-delete"
                                    title="Delete"
                                    aria-label={`Delete ${displayName}`}
                                    onClick={() => handleDelete(functionalValueDriver)}
                                  >
                                    <Trash2 />
                                  </UiButton>
                                </div>
                              </td>
                            </tr>

                            {isExpanded
                              ? subDriversList.length > 0
                                ? subDriversList.map((subDriver) => (
                                    <tr
                                      key={`sub-${subDriver.id}`}
                                      className="rdm-row-clickable vd-sub-row"
                                      onClick={() => openViewModal(subDriver, "child")}
                                    >
                                      <td />
                                      <td />
                                      <td>
                                        <span className="app-td__desc vd-sub-name">
                                          {subDriver.name}
                                        </span>
                                      </td>
                                      <td>
                                        {subDriver.description ? (
                                          <span className="app-td__desc" title={subDriver.description}>
                                            {subDriver.description}
                                          </span>
                                        ) : (
                                          <EmptyCell />
                                        )}
                                      </td>
                                      <td>
                                        <span className="app-td__meta">Sub-driver</span>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            subDriver.isActive
                                              ? "app-status app-status--success"
                                              : "app-status app-status--muted"
                                          }
                                        >
                                          {subDriver.isActive ? "Active" : "Inactive"}
                                        </span>
                                      </td>
                                      <td>
                                        <EmptyCell />
                                      </td>
                                    </tr>
                                  ))
                                : (
                                    <tr className="vd-sub-row">
                                      <td colSpan={7} className="text-center">
                                        <span className="app-td__desc">No sub-drivers available</span>
                                      </td>
                                    </tr>
                                  )
                              : null}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <UiPagination
                page={apiPageInfo.pageNumber}
                totalPages={apiPageInfo.totalPages}
                onPageChange={goToPage}
              />
            </Fragment>
          )}
        </div>
      </SaListPageShell>

      {/* Create/Edit Modal */}
      <Modal
        show={showModal}
        onHide={handleClose}
        centered
        size="lg"
        dialogClassName="rounded-3"
      >
        <Modal.Header closeButton className="dashboard-modal-header">
          {functionalValueDriverForm.id > 0 ? (
            <>
              <Settings className="h-5 w-5 me-2" />
              <Modal.Title>Edit Functional Value Driver</Modal.Title>
            </>
          ) : (
            <>
              <Settings className="h-5 w-5 me-2" />
              <Modal.Title>Create Functional Value Driver</Modal.Title>
            </>
          )}
        </Modal.Header>

        <Form onSubmit={handleFunctionalValueDriverSubmit}>
          <Modal.Body className="px-4">
            <Row>
              <Col md={12}>
                <Form.Group controlId="formName" className="mb-3">
                  <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={functionalValueDriverForm.name}
                    onChange={handleFunctionalValueDriverChange}
                    required
                    placeholder="Enter functional value driver name"
                    maxLength="100"
                    style={{ height: "38px" }}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group controlId="formDescription" className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    value={functionalValueDriverForm.description}
                    onChange={handleFunctionalValueDriverChange}
                    placeholder="Enter description"
                    maxLength="500"
                    rows={3}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group controlId="formIsActive" className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <div className="mt-0">
                    <Form.Check
                      type="checkbox"
                      name="isActive"
                      checked={functionalValueDriverForm.isActive}
                      onChange={handleFunctionalValueDriverChange}
                      label="Active"
                    />
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Sub Functional Value Drivers Section */}
            <div className="mt-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0">
                  <Layers className="me-2" />
                  Sub Functional Value Drivers
                </h6>
                <Button
                  onClick={handleAddSubDriver}
                  className="btn btn-dashboard-primary btn-sm"
                  style={{ height: "32px" }}
                  size="sm"
                >
                  <Plus className="me-1" />
                  Add More
                </Button>
              </div>

              {/* Sub Driver Fields */}
              {functionalValueDriverForm.functionalSubValueDrivers && functionalValueDriverForm.functionalSubValueDrivers.map((subDriver, index) => (
                <div key={index} className="border rounded p-3 mb-2 dashboard-form-section">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0">Sub Value Driver {index + 1}</h6>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="cap-mgmt-icon-btn"
                      onClick={() => handleDeleteSubDriver(index)}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverName${index}`} className="mb-2">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                          type="text"
                          value={subDriver.name}
                          onChange={(e) => handleSubDriverFieldChange(index, 'name', e.target.value)}
                          placeholder="Enter sub value driver name"
                          maxLength="100"
                          style={{ height: "38px" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverDescription${index}`} className="mb-2">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          type="text"
                          value={subDriver.description}
                          onChange={(e) => handleSubDriverFieldChange(index, 'description', e.target.value)}
                          placeholder="Enter description"
                          maxLength="200"
                          style={{ height: "38px" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group controlId={`subDriverIsActive${index}`} className="mb-0">
                        <Form.Label>Status</Form.Label>
                        <div className="mt-1">
                          <Form.Check
                            type="checkbox"
                            checked={subDriver.isActive !== undefined ? subDriver.isActive : true}
                            onChange={(e) => handleSubDriverFieldChange(index, 'isActive', e.target.checked)}
                            label="Active"
                          />
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              ))}

            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="outline-secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="btn btn-dashboard-primary"
            >
              Save Functional Value Driver
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation */}
      <Modal show={showDeleteModal} onHide={cancelDelete} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteType === "subDriver" ? (
            <>
              Are you sure you want to delete this sub functional value driver?
            </>
          ) : (
            <>
              Are you sure you want to delete{" "}
              <strong>{itemToDelete?.name}</strong>?
              <br />
              <strong>This will also delete all associated sub-drivers.</strong>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cancelDelete}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <RecordDetailModal
        show={showViewModal}
        onHide={closeViewModal}
        title={
          viewRecord?.name ||
          (viewMode === "child"
            ? "Sub Value Driver Details"
            : "Functional Value Driver Details")
        }
        subtitle={
          viewMode === "child"
            ? "Sub value driver record"
            : "Functional value driver record"
        }
        fields={
          viewMode === "child"
            ? [
                { label: "Name", value: viewRecord?.name },
                {
                  label: "Description",
                  value: viewRecord?.description,
                  fullWidth: true,
                },
                { label: "Type", value: "Sub Value Driver" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
            : [
                { label: "Name", value: viewRecord?.name },
                {
                  label: "Description",
                  value: viewRecord?.description,
                  fullWidth: true,
                },
                { label: "Type", value: "Functional Value Driver" },
                { label: "Active", value: Boolean(viewRecord?.isActive) },
              ]
        }
        groups={
          viewMode === "parent"
            ? [
                {
                  title: "Sub Value Drivers",
                  content: (() => {
                    const children = viewRecord ? getSubDrivers(viewRecord) : [];
                    if (!children.length) {
                      return <span className="rdm-empty">None</span>;
                    }
                    return children.map((sub) => (
                      <div key={sub.id} className="rdm-nested-card">
                        <div className="rdm-nested-card-title">{sub.name}</div>
                        {sub.description ? (
                          <div className="rdm-nested-meta">{sub.description}</div>
                        ) : null}
                        <div className="rdm-nested-meta">
                          {sub.isActive ? "Active" : "Inactive"}
                        </div>
                      </div>
                    ));
                  })(),
                },
              ]
            : []
        }
        actions={
          viewMode === "parent"
            ? [
                {
                  type: "edit",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleShowFunctionalValueDriver(record);
                  },
                },
                {
                  type: "delete",
                  onClick: () => {
                    const record = viewRecord;
                    closeViewModal();
                    if (record) handleDelete(record);
                  },
                },
              ]
            : []
        }
      />

    </DashboardShell>
  );
};

export default FunctionalValueDrivers;
